import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ChatBubbleLeftIcon, PencilIcon, TrashIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { documentCommentService, DocumentComment, CreateDocumentCommentRequest, UpdateDocumentCommentRequest } from '../../services/documentCommentService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../UI/ToastProvider';

interface DocumentCommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
}

interface CommentFormData {
  comment: string;
  parentCommentId?: string;
}

export const DocumentCommentsModal: React.FC<DocumentCommentsModalProps> = ({
  isOpen,
  onClose,
  documentId,
  documentTitle
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Estados
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [deletingComments, setDeletingComments] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<CommentFormData>({ comment: '' });
  const [replyToComment, setReplyToComment] = useState<string | null>(null);
  const [editTexts, setEditTexts] = useState<{[key: string]: string}>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  // Refs
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Carregar comentários
  const loadComments = useCallback(async () => {
    if (!documentId) return;
    
    setLoading(true);
    try {
      const commentsData = await documentCommentService.getDocumentComments(documentId);
      setComments(commentsData);
    } catch (error: any) {
      console.error('Erro ao carregar comentários:', error);
      addToast('Erro ao carregar comentários', 'error');
    } finally {
      setLoading(false);
    }
  }, [documentId, addToast]);

  // Carregar comentários quando modal abrir
  useEffect(() => {
    if (isOpen) {
      loadComments();
    }
  }, [isOpen, loadComments]);

  // Focar no textarea quando modal abrir
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Função para organizar comentários em árvore
  const organizeComments = (comments: DocumentComment[]): DocumentComment[] => {
    const commentMap = new Map<string, DocumentComment & { children: DocumentComment[] }>();
    const rootComments: (DocumentComment & { children: DocumentComment[] })[] = [];

    // Criar mapa de comentários
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, children: [] });
    });

    // Organizar em árvore
    comments.forEach(comment => {
      const commentWithChildren = commentMap.get(comment.id)!;
      
      if (comment.parentCommentId) {
        const parent = commentMap.get(comment.parentCommentId);
        if (parent) {
          parent.children.push(commentWithChildren);
        }
      } else {
        rootComments.push(commentWithChildren);
      }
    });

    // Ordenar comentários principais do mais recente para o mais antigo
    rootComments.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    // Ordenar respostas de cada comentário do mais recente para o mais antigo
    const sortChildren = (comment: DocumentComment & { children: DocumentComment[] }) => {
      if (comment.children && comment.children.length > 0) {
        comment.children.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        comment.children.forEach(child => sortChildren(child as DocumentComment & { children: DocumentComment[] }));
      }
    };

    rootComments.forEach(comment => sortChildren(comment));

    return rootComments;
  };

  // Enviar comentário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.comment.trim() || formData.comment.length > 400) {
      addToast('Comentário deve ter entre 1 e 400 caracteres', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const request: CreateDocumentCommentRequest = {
        documentId,
        comment: formData.comment.trim(),
        parentCommentId: replyToComment || undefined
      };

      const newComment = await documentCommentService.createComment(request);
      
      // Garantir que o comentário tenha o nome do usuário logado
      const commentWithUser = {
        ...newComment,
        userName: newComment.userName || user?.name || user?.email || 'Usuário'
      };
      
      // Adicionar novo comentário à lista
      setComments(prev => [...prev, commentWithUser]);
      
      // Limpar formulário
      setFormData({ comment: '' });
      setReplyToComment(null);
      
      addToast('Comentário adicionado com sucesso', 'success');
      
      // Focar no textarea
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      
    } catch (error: any) {
      console.error('Erro ao criar comentário:', error);
      addToast('Erro ao criar comentário', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Editar comentário
  const handleEdit = async (commentId: string) => {
    const newComment = editTexts[commentId];
    if (!newComment?.trim() || newComment.length > 400) {
      addToast('Comentário deve ter entre 1 e 400 caracteres', 'warning');
      return;
    }

    try {
      const request: UpdateDocumentCommentRequest = {
        id: commentId,
        comment: newComment.trim()
      };

      const updatedComment = await documentCommentService.updateComment(request);
      
      // Garantir que o comentário editado tenha o nome do usuário
      const commentWithUser = {
        ...updatedComment,
        userName: updatedComment.userName || user?.name || user?.email || 'Usuário'
      };
      
      // Atualizar comentário na lista
      setComments(prev => 
        prev.map(comment => 
          comment.id === commentId ? commentWithUser : comment
        )
      );
      
      setEditingComment(null);
      setEditTexts(prev => {
        const newTexts = { ...prev };
        delete newTexts[commentId];
        return newTexts;
      });
      addToast('Comentário atualizado com sucesso', 'success');
      
    } catch (error: any) {
      console.error('Erro ao editar comentário:', error);
      addToast('Erro ao editar comentário', 'error');
    }
  };

  // Deletar comentário
  const handleDelete = async (commentId: string) => {
    setDeletingComments(prev => new Set(prev).add(commentId));
    
    try {
      await documentCommentService.deleteComment(commentId);
      
      // Remover comentário da lista
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      
      addToast('Comentário excluído com sucesso', 'success');
      
    } catch (error: any) {
      console.error('Erro ao excluir comentário:', error);
      addToast('Erro ao excluir comentário', 'error');
    } finally {
      setDeletingComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
      setShowDeleteConfirm(null);
    }
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime())) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Agora mesmo';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h atrás`;
    } else if (diffInHours < 168) { // 7 dias
      return `${Math.floor(diffInHours / 24)}d atrás`;
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  // Renderizar comentário
  const renderComment = (comment: DocumentComment & { children?: DocumentComment[] }, isReply = false) => {
    const isOwner = user?.id === comment.userId;
    const isEditing = editingComment === comment.id;
    const isDeleting = deletingComments.has(comment.id);
    
    // Determinar o nome do usuário com fallbacks
    const userName = comment.userName || comment.user?.name || comment.user?.email || 'Usuário';
    const userInitial = comment.userName?.charAt(0)?.toUpperCase() || 
                       comment.user?.name?.charAt(0)?.toUpperCase() || 
                       comment.user?.email?.charAt(0)?.toUpperCase() || 
                       'U';

    return (
      <div key={comment.id} className={`${isReply ? 'ml-8 mt-3' : 'mb-4'}`}>
        <div className="bg-gray-50 rounded-lg p-4 border">
          {/* Cabeçalho do comentário */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {userInitial}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {userName}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(comment.updatedAt)}
                  {comment.updated && (
                    <span className="ml-1 text-blue-600">(editado)</span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Ações do comentário */}
            {isOwner && !isEditing && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setEditingComment(comment.id)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Editar comentário"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                                 <button
                   onClick={() => setShowDeleteConfirm(comment.id)}
                   disabled={isDeleting}
                   className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                   title="Excluir comentário"
                 >
                   {isDeleting ? (
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                   ) : (
                     <TrashIcon className="h-4 w-4" />
                   )}
                 </button>
              </div>
            )}
          </div>

                     {/* Conteúdo do comentário */}
           {isEditing ? (
             <div className="space-y-2">
               <textarea
                 value={editTexts[comment.id] || comment.comment}
                 onChange={(e) => setEditTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                 className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                 rows={3}
                 maxLength={400}
                 autoFocus
               />
               <div className="flex justify-between items-center">
                 <span className="text-xs text-gray-500">
                   {(editTexts[comment.id] || comment.comment).length}/400 caracteres
                 </span>
                 <div className="flex space-x-2">
                   <button
                     onClick={() => {
                       setEditingComment(null);
                       setEditTexts(prev => {
                         const newTexts = { ...prev };
                         delete newTexts[comment.id];
                         return newTexts;
                       });
                     }}
                     className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                   >
                     Cancelar
                   </button>
                   <button
                     onClick={() => handleEdit(comment.id)}
                     className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                   >
                     Salvar
                   </button>
                 </div>
               </div>
             </div>
          ) : (
            <div>
              <p className="text-gray-800 whitespace-pre-wrap">{comment.comment}</p>
              
              {/* Botão de responder */}
              {!isReply && (
                <button
                  onClick={() => {
                    setReplyToComment(comment.id);
                    textareaRef.current?.focus();
                  }}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Responder
                </button>
              )}
            </div>
          )}
        </div>

        {/* Respostas */}
        {comment.children && comment.children.length > 0 && (
          <div className="mt-3">
            {comment.children.map(child => renderComment(child, true))}
          </div>
        )}
      </div>
    );
  };

  const organizedComments = organizeComments(comments);

  return (
    <>
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white rounded-lg shadow-xl">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <ChatBubbleLeftIcon className="h-6 w-6 text-blue-600" />
              <div>
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Comentários
                </Dialog.Title>
                <p className="text-sm text-gray-500 truncate max-w-md">
                  {documentTitle}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex flex-col h-96">
            {/* Lista de comentários */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : organizedComments.length === 0 ? (
                <div className="text-center py-8">
                  <ChatBubbleLeftIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum comentário ainda</p>
                  <p className="text-sm text-gray-400">Seja o primeiro a comentar!</p>
                </div>
              ) : (
                <div>
                  {organizedComments.map(comment => renderComment(comment))}
                  <div ref={commentsEndRef} />
                </div>
              )}
            </div>

            {/* Formulário de comentário */}
            <div className="border-t p-6">
              <form onSubmit={handleSubmit} className="space-y-3">
                {replyToComment && (
                  <div className="flex items-center space-x-2 text-sm bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span className="font-medium text-yellow-800">Respondendo ao comentário</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyToComment(null)}
                      className="text-yellow-500 hover:text-yellow-700 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                
                <div className="flex space-x-3">
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={formData.comment}
                      onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                      placeholder="Escreva seu comentário..."
                      className="w-full p-3 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      maxLength={400}
                      disabled={submitting}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">
                        {formData.comment.length}/400 caracteres
                      </span>
                      {formData.comment.length > 400 && (
                        <span className="text-xs text-red-500">
                          Limite de caracteres excedido
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={!formData.comment.trim() || formData.comment.length > 400 || submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <PaperAirplaneIcon className="h-4 w-4" />
                    )}
                    <span>Enviar</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
                 </Dialog.Panel>
       </div>
     </Dialog>

     {/* Modal de confirmação de exclusão - Fora do modal principal */}
     <Dialog open={showDeleteConfirm !== null} onClose={() => setShowDeleteConfirm(null)} className="relative z-[100]">
       <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
       
       <div className="fixed inset-0 flex items-center justify-center p-4">
         <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-lg shadow-xl">
           <div className="p-6">
             <div className="flex items-center space-x-3 mb-4">
               <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                 <TrashIcon className="h-6 w-6 text-red-600" />
               </div>
               <div>
                 <Dialog.Title className="text-lg font-semibold text-gray-900">
                   Excluir comentário
                 </Dialog.Title>
                 <p className="text-sm text-gray-500">
                   Esta ação não pode ser desfeita
                 </p>
               </div>
             </div>
             
             <p className="text-gray-700 mb-6">
               Tem certeza que deseja excluir este comentário?
             </p>
             
             <div className="flex justify-end space-x-3">
               <button
                 onClick={() => setShowDeleteConfirm(null)}
                 className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
               >
                 Cancelar
               </button>
               <button
                 onClick={() => showDeleteConfirm && handleDelete(showDeleteConfirm)}
                 className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
               >
                 Excluir
               </button>
             </div>
           </div>
         </Dialog.Panel>
       </div>
     </Dialog>
   </>
   );
 };
